import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  GetCall,
  initialPage,
  Page,
  PaginationRequestParam,
} from "../../api-model/api";
import { CoreRequest } from "../../config/serviceInstances";
import { ActionType, handleActionWithFeedback } from "../../store/errors";
import { EmployeeAvatarDto } from "../../api-model/organization/company";
import { TemplateType } from "../../organization/companies/detail/dpf";
import {
  createWorkContractSubTask,
  deleteWorkContractSubTask,
  listWorkContractSubTasks,
  updateWorkContractSubTask,
  WorkContractSubTask,
} from "./sub-tasks";

export enum WorkContractTaskStatus {
  DONE = "DONE",
  OPEN = "OPEN",
}

export interface TaskProject {
  uuid: string;
  description: string;
  dueDate: string;
  category: string | TemplateType;
  subjectWorkContractId?: string;
  subject?: EmployeeAvatarDto;
  companyId?: string;
  subTasks?: Array<WorkContractSubTask>;
}

export interface WorkContractTaskParams extends PaginationRequestParam {
  category?: TemplateType;
}

export const listTaskProjectsByCategory = createAsyncThunk<
  Page<TaskProject>,
  string
>("/employees/contract/tasksBoard", async (categories) => {
  const { data } = await CoreRequest.get("/companies/task-projects", {
    params: {
      page: 0,
      pageSize: 25,
      sort: "description",
      categories,
    },
  });

  return data;
});

export const deleteTaskProject = createAsyncThunk<TaskProject, string>(
  "/employees/contract/tasks/delete",
  async (uuid, thunkApi) => {
    return handleActionWithFeedback<TaskProject>(
      () => CoreRequest.delete(`/companies/task-projects/${uuid}`),
      thunkApi,
      ActionType.delete,
      "resource.project"
    );
  }
);

export const createTaskProject = createAsyncThunk<TaskProject, TaskProject>(
  "/employees/contract/tasks/create",
  async (payload, thunkApi) => {
    return handleActionWithFeedback<TaskProject>(
      () =>
        CoreRequest.post(
          `/companies/${payload.companyId}/task-projects`,
          payload
        ),
      thunkApi,
      ActionType.create,
      "resource.project"
    );
  }
);

export type WorkContractTasksState = {
  tableView: GetCall<WorkContractSubTask>;
  boardView: Record<string, GetCall<TaskProject>>;
};

const initialState: WorkContractTasksState = {
  tableView: {
    loading: false,
    error: false,
    errorMessage: "",
    page: initialPage,
  },
  boardView: {},
};

const tasks = createSlice({
  name: "employees/current",
  initialState: initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(listWorkContractSubTasks.pending, (state) => {
      state.tableView.loading = true;
    });
    builder.addCase(listWorkContractSubTasks.fulfilled, (state, action) => {
      state.tableView.loading = false;
      state.tableView.error = false;
      state.tableView.page = action.payload;
    });
    builder.addCase(listWorkContractSubTasks.rejected, (state) => {
      state.tableView.loading = false;
      state.tableView.error = true;
    });

    builder.addCase(updateWorkContractSubTask.pending, (state) => {
      state.tableView.loading = true;
    });
    builder.addCase(updateWorkContractSubTask.fulfilled, (state, action) => {
      const { category, taskProjectId } = action.payload;
      state.tableView.loading = false;
      state.tableView.error = false;
      const taskIndex = state.tableView.page.content.findIndex(
        (item) => item.uuid === action.payload.uuid
      );
      state.tableView.page.content[taskIndex] = action.payload;

      state.boardView[category] = state.boardView[category] ?? {};
      state.boardView[category].page =
        state.boardView[category].page ?? initialPage;

      const taskProjectIndex = state.boardView[category].page.content.findIndex(
        (task) => task.uuid === taskProjectId
      );
      const subTaskIndex = state.boardView[category].page.content[
        taskProjectIndex
      ]?.subTasks?.findIndex((task) => task.uuid === action.payload.uuid);

      if (subTaskIndex === undefined || subTaskIndex === -1) {
        return;
      }

      const subTasks =
        state.boardView[category].page.content[taskProjectIndex].subTasks;

      if (!!subTasks?.length) {
        subTasks[subTaskIndex] = action.payload;
      }
    });
    builder.addCase(updateWorkContractSubTask.rejected, (state) => {
      state.tableView.loading = false;
      state.tableView.error = true;
    });

    builder.addCase(createWorkContractSubTask.fulfilled, (state, action) => {
      const category = action.payload.category;
      state.tableView.loading = false;
      state.tableView.error = false;
      state.tableView.page.content.unshift(action.payload);

      state.boardView[category] = state.boardView[category] || {
        page: initialPage,
      };
      const taskIndex = state.boardView[category].page.content.findIndex(
        (task) => task.uuid === action.meta.arg.taskId
      );

      if (taskIndex === -1) {
        return;
      }
      state.boardView[category].page.content[taskIndex].subTasks = [
        action.payload,
        ...(state.boardView[category].page.content[taskIndex].subTasks ?? []),
      ];
    });

    builder.addCase(deleteWorkContractSubTask.pending, (state) => {
      state.tableView.loading = true;
    });
    builder.addCase(deleteWorkContractSubTask.fulfilled, (state, action) => {
      const { category, taskProjectId } = action.payload;
      state.tableView.loading = false;
      state.tableView.error = false;
      state.tableView.page.content = state.tableView.page.content.filter(
        (f) => f.uuid !== action.payload.uuid
      );

      state.boardView[category] = state.boardView[category] || {
        page: initialPage,
      };
      const taskIndex = state.boardView[category].page.content.findIndex(
        (task) => task.uuid === taskProjectId
      );

      if (taskIndex > -1) {
        state.boardView[category].page.content[taskIndex].subTasks =
          state.boardView[category].page.content?.[taskIndex]?.subTasks?.filter(
            (item) => {
              return item.uuid !== action.payload.uuid;
            }
          );
      }
    });
    builder.addCase(deleteWorkContractSubTask.rejected, (state) => {
      state.tableView.loading = false;
      state.tableView.error = true;
    });

    builder.addCase(deleteTaskProject.fulfilled, (state, action) => {
      const category = action.payload.category;
      state.boardView[category] = state.boardView[category] || {
        page: initialPage,
      };
      state.boardView[category].loading = false;
      state.boardView[category].error = false;
      state.boardView[category].page.content = state.boardView[
        category
      ].page.content.filter((f) => f.uuid !== action.payload.uuid);
    });

    builder.addCase(createTaskProject.pending, (state, action) => {
      const category = action.meta.arg.category;
      const categoryBoardView = state.boardView[category];

      if (categoryBoardView) {
        state.boardView[category].loading = true;
      }
    });
    builder.addCase(createTaskProject.fulfilled, (state, action) => {
      const category = action.meta.arg.category;
      const categoryBoardView = state.boardView[category];

      if (categoryBoardView) {
        state.boardView[category].loading = false;
        state.boardView[category].error = false;
        state.boardView[category] = state.boardView[category] || {
          page: initialPage,
        };
        state.boardView[category].page.content = [
          action.payload,
          ...state.boardView[category].page.content,
        ];
      }
    });
    builder.addCase(createTaskProject.rejected, (state, action) => {
      const category = action.meta.arg.category;
      const categoryBoardView = state.boardView[category];

      if (categoryBoardView) {
        state.boardView[category].loading = false;
        state.boardView[category].error = true;
      }
    });

    // TasksBoard view
    builder.addCase(listTaskProjectsByCategory.pending, (state, action) => {
      const category = action.meta.arg;
      state.boardView[category] = state.boardView[category] ?? {};
      state.boardView[category].page = initialPage;
      state.boardView[category].loading = true;
    });
    builder.addCase(listTaskProjectsByCategory.fulfilled, (state, action) => {
      const category = action.meta.arg;
      state.boardView[category] = state.boardView[category] ?? {};
      state.boardView[category].loading = false;
      state.boardView[category].error = false;
      state.boardView[category].page = action.payload;
    });
    builder.addCase(listTaskProjectsByCategory.rejected, (state, action) => {
      const category = action.meta.arg;
      state.boardView[category] = state.boardView[category] ?? {};
      state.boardView[category].loading = false;
      state.boardView[category].error = true;
    });
  },
});

export default tasks;
